import { useId, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './FormField.module.scss';

export interface FormFieldRenderProps {
  id: string;
  /**
   * `id` do rotulo visivel.
   *
   * `<label for>` so nomeia controle de formulario nativo. Um campo construido
   * sobre outro elemento — um editor de texto rico em `contenteditable`, por
   * exemplo — precisa apontar para o rotulo com `aria-labelledby`, senao chega
   * ao leitor de tela sem nome nenhum.
   */
  labelId: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
  'aria-required': boolean | undefined;
}

export interface FormFieldProps {
  /** Rotulo. Fica **acima** do campo e sempre visivel — placeholder nao e rotulo. */
  label: string;
  /** Texto de apoio, abaixo do campo. */
  hint?: string;
  /** Mensagem de erro. Presente = campo em estado invalido. */
  error?: string;
  required?: boolean;
  /** Esconde visualmente o "(obrigatório)" ao lado do rótulo, mantendo o anúncio para leitor de tela. */
  hideRequiredText?: boolean;
  /**
   * Esconde visualmente o rotulo, mantendo-o no DOM e ligado ao campo.
   *
   * Para a **linha repetida** — sete dias com "Inicio" e "Fim" —, em que
   * repetir o rotulo em cada linha seria ruido; o cabecalho da coluna nomeia, e
   * o leitor de tela continua ouvindo o rotulo campo a campo.
   */
  hideLabel?: boolean;
  /**
   * O proprio controle traz o rotulo — a moldura nao desenha nenhum.
   *
   * E o caso da caixa de selecao, que nasce com o rotulo clicavel ao lado. Sem
   * isto, o campo fica com dois rotulos e o leitor de tela anuncia o nome
   * duplicado: "Tabagismo Tabagismo".
   */
  labelFromControl?: boolean;
  className?: string;
  /** Recebe os atributos de acessibilidade a repassar ao controle. */
  children: (props: FormFieldRenderProps) => ReactNode;
}

/**
 * Moldura de campo de formulario.
 *
 * Concentra a acessibilidade que costuma ser esquecida campo a campo: o `id` que
 * liga rotulo e controle, o `aria-describedby` que liga a mensagem de erro, e o
 * `aria-invalid`.
 *
 * A obrigatoriedade e marcada **em texto**, nao so por asterisco: leitor de tela
 * anuncia "obrigatório", e quem enxerga tambem le a palavra.
 */
export function FormField({
  label,
  hint,
  error,
  required,
  hideRequiredText,
  hideLabel,
  labelFromControl,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={cn(styles.field, error && styles.invalid, className)}>
      {labelFromControl ? null : (
        <label id={labelId} className={cn(styles.label, hideLabel && styles.srOnly)} htmlFor={id}>
          {label}
          {required ? (
            <span className={cn(styles.required, hideRequiredText && styles.srOnly)}> (obrigatório)</span>
          ) : null}
        </label>
      )}

      {children({
        id,
        labelId,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })}

      {/*
        Apoio e erro moram na **mesma** faixa, sempre a terceira do campo.

        Isso e o que permite alinhar os campos de uma linha entre si: com a
        mensagem numa faixa propria, uma linha de erro embaixo de um campo nao
        empurra mais a caixa do vizinho para cima. Vazia, a faixa nao ocupa
        altura nenhuma (`:empty` some).
      */}
      <div className={styles.mensagem}>
        {hint ? (
          <p className={styles.hint} id={hintId}>
            {hint}
          </p>
        ) : null}

        {error ? (
          <p className={styles.error} id={errorId}>
            {/* O erro nunca e sinalizado so por cor: tem icone e texto. */}
            <Icon as={AlertCircle} size={16} /> {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
